import { Layout } from '@/components/ui/layout';
import { planManagementColumns } from './table/columns';
import { PlanManagementDataTable } from './table/data-table';
import { PrivateAdminRoute } from '@/components/organisms/route/admin';
import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useLanguage } from '@/hooks/useLanguage';
import type { Plan } from '@/graphql/types/admin';
import { Ellipsis } from 'lucide-react';
import { Button, ButtonLoading } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InputWithLabel } from '@/components/ui/input';
import { TextareaWithLabel } from '@/components/ui/textarea';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { PlanManagementTableLoading } from './table/loading';
import { Alert } from '@/components/molecules/alert';
import { useAlert } from '@/hooks/useAlert';
import { QUERY_PLANS } from '@/graphql/queries/plans';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  MUTATION_CREATE_PLAN,
  MUTATION_DELETE_PLAN,
  MUTATION_UPDATE_PLAN,
} from '@/graphql/mutation/plan';
import { Add, Delete } from '@mui/icons-material';

// ENUMs para módulos disponíveis
const DEFAULT_MODULES = ['DASHBOARD', 'METHOD', 'REPORTS', 'BLOG', 'FAQ'];

interface CreateFormValues {
  name: string;
  description: string;
  price: number;
  periodicity: 'MONTHLY' | 'ANNUAL';
  is_active: boolean;
  modules: string[];
}

interface UpdateFormValues {
  id?: string;
  name?: string;
  description?: string;
  price?: number;
  periodicity?: 'MONTHLY' | 'ANNUAL';
  is_active?: boolean;
  modules?: string[];
}

export default function PlanManagementPage() {
  const apolloClient = useApolloClient();

  const { currentLanguage } = useLanguage();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [data, setData] = useState<Plan[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Plan | null>(null);
  const [dialogVariant, setDialogVariant] = useState<'create' | 'update'>('update');
  const [availableModules, setAvailableModules] = useState<string[]>(DEFAULT_MODULES);

  const createSchema = yup.object().shape({
    name: yup.string().required('Nome é obrigatório'),
    description: yup.string().required('Descrição é obrigatória'),
    price: yup.number().required('Preço é obrigatório'),
    periodicity: yup
      .mixed<'MONTHLY' | 'ANNUAL'>()
      .oneOf(['MONTHLY', 'ANNUAL'])
      .required('Periodicidade é obrigatória'),
    is_active: yup.boolean().required('Status é obrigatório'),
    modules: yup.array().of(yup.string()).required('Funcionalidades são obrigatórias'),
  });

  const updateSchema = yup.object().shape({
    name: yup.string(),
    description: yup.string(),
    price: yup.number(),
    periodicity: yup.mixed<'MONTHLY' | 'ANNUAL'>().oneOf(['MONTHLY', 'ANNUAL']),
    is_active: yup.boolean(),
    modules: yup.array().of(yup.string()),
  });

  const schema = useMemo(() => (dialogVariant === 'create' ? createSchema : updateSchema), [
    dialogVariant,
  ]);

  const createInitialValues: CreateFormValues = {
    name: '',
    description: '',
    price: 0,
    periodicity: 'MONTHLY',
    is_active: false,
    modules: [],
  };

  const updateInitialValues: UpdateFormValues = {
    id: selectedItem?.id || '',
    name: selectedItem?.name || '',
    description: selectedItem?.description || '',
    price: selectedItem?.price || 0,
    periodicity: selectedItem?.periodicity || 'MONTHLY',
    is_active: selectedItem?.is_active || false,
    modules: selectedItem?.modules || [],
  };

  const initialValues = useMemo(
    () => (dialogVariant === 'create' ? createInitialValues : updateInitialValues),
    [dialogVariant, selectedItem]
  );

  const handleRemoveModule = (moduleName: string) => {
    setAvailableModules(availableModules.filter((m) => m !== moduleName));
  };

  const normalizedData = useMemo(() => {
    return data.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      price: item.price,
      periodicity: item.periodicity,
      is_active: item.is_active,
      modules: item.modules,
      actions: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem
              onClick={() => {
                resetForm();
                setDialogVariant('update');
                setOpenDialog(true);
                setSelectedItem(item);
              }}
            >
              <span>Editar</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                resetForm();
                setOpenRemoveDialog(true);
                setSelectedItem(item);
              }}
            >
              <span>Remover</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }));
  }, [data]);

  const resetForm = () => {
    setSelectedItem(null);
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setLoadingForm(true);

      if (dialogVariant === 'update') {
        const updateData = {
          name: values.name,
          description: values.description,
          price: Number(values.price),
          periodicity: values.periodicity,
          is_active: Boolean(values.is_active),
          modules: values.modules,
        };
        await apolloClient.mutate<{ updatePlan: Plan }>({
          mutation: MUTATION_UPDATE_PLAN,
          variables: { updatePlanId: selectedItem?.id, input: updateData },
        });
        setOpenDialog(false);
        showAlert('success', 'Plano criado com sucesso');
      }

      if (dialogVariant === 'create') {
        const createdData = {
          name: values.name,
          description: values.description,
          price: Number(values.price),
          periodicity: values.periodicity,
          is_active: Boolean(values.is_active),
          modules: values.modules,
        };
        await apolloClient.mutate<{ createPlan: Plan }>({
          mutation: MUTATION_CREATE_PLAN,
          variables: { input: createdData },
        });
        setOpenDialog(false);
        showAlert('success', 'Plano criado com sucesso');
      }

      await getPlans();

      resetForm();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  const handleRemovePlan = async () => {
    try {
      setLoadingForm(true);

      await apolloClient.mutate<{ deletePlan: Plan }>({
        mutation: MUTATION_DELETE_PLAN,
        variables: { deletePlanId: selectedItem?.id },
      });

      await getPlans();

      setOpenRemoveDialog(false);
      showAlert('success', 'Plano removido com sucesso');

      resetForm();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  const getPlans = async () => {
    const { data } = await apolloClient.query<{ plans: Plan[] }>({
      query: QUERY_PLANS,
    });
    setData(data.plans);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        await getPlans();
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [currentLanguage, apolloClient]);

  return (
    <PrivateAdminRoute>
      <Layout
        title="Gestão de planos"
        customButton={
          <Button
            variant="default"
            onClick={() => {
              resetForm();
              setDialogVariant('create');
              setOpenDialog(true);
            }}
          >
            <Add />
            Novo Plano
          </Button>
        }
      >
        {loadingData ? (
          <PlanManagementTableLoading />
        ) : (
          <PlanManagementDataTable columns={planManagementColumns} data={normalizedData} />
        )}
      </Layout>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="gap-6">
          <DialogHeader className="w-fit">
            <DialogTitle>Editar Plano</DialogTitle>
          </DialogHeader>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={schema}
            enableReinitialize={dialogVariant === 'update'}
            key={`${dialogVariant}-${selectedItem?.id}`}
          >
            {({ values, errors, setFieldValue }) => (
              <Form className="flex flex-col gap-6 w-full">
                <InputWithLabel
                  label="Nome do Plano"
                  id="name"
                  name="name"
                  defaultValue={values.name}
                  onChange={(event) => setFieldValue('name', event.target.value)}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  className="w-full max-w-full"
                />

                <TextareaWithLabel
                  label="Descrição do plano"
                  id="description"
                  name="description"
                  rows={10}
                  defaultValue={values.description}
                  onChange={(event) => setFieldValue('description', event.target.value)}
                  error={Boolean(errors.description)}
                  helperText={errors.description}
                />

                <div className="flex gap-4">
                  <div className="flex flex-col justify-between gap-2 w-full max-w-full">
                    <Label htmlFor="plan_periodicity">Periodicidade</Label>
                    <Select
                      name="periodicity"
                      value={values.periodicity}
                      onValueChange={(value) => setFieldValue('periodicity', value)}
                    >
                      <SelectTrigger className="h-8 w-full max-w-full">
                        <SelectValue placeholder={values.periodicity} />
                      </SelectTrigger>
                      <SelectContent side="bottom">
                        <SelectItem value="MONTHLY">Mensal</SelectItem>
                        <SelectItem value="ANNUAL">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <InputWithLabel
                    label="Preço (R$)"
                    id="price"
                    name="price"
                    defaultValue={values.price}
                    onChange={(event) => setFieldValue('price', event.target.value)}
                    error={Boolean(errors.price)}
                    helperText={errors.price}
                    className="w-full max-w-full"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    name="is_active"
                    defaultChecked={values.is_active}
                    onCheckedChange={(checked) => setFieldValue('is_active', checked)}
                  />
                  <Label htmlFor="is_active">{values.is_active ? 'Ativo' : 'Inativo'}</Label>
                </div>

                <div className="flex flex-col gap-4">
                  <Label htmlFor="plan_modules">Funcionalidades</Label>

                  <div className="grid grid-cols-2 gap-4">
                    {availableModules.map((moduleName) => (
                      <div key={moduleName} className="flex items-center gap-2">
                        <Checkbox
                          id={`plan_module_${moduleName}`}
                          checked={values.modules?.includes(moduleName) || false}
                          onCheckedChange={(checked) =>
                            setFieldValue(
                              'modules',
                              checked
                                ? [...(values.modules || []), moduleName]
                                : (values.modules || []).filter((m) => m !== moduleName)
                            )
                          }
                        />
                        <Label htmlFor={`plan_module_${moduleName}`} className="flex-1 capitalize">
                          {moduleName.toLowerCase()}
                        </Label>
                        {availableModules.length > 5 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => handleRemoveModule(moduleName)}
                          >
                            <Delete className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <DialogFooter className="flex-col">
                  <Button variant="outline" onClick={() => setOpenDialog(false)}>
                    Cancelar
                  </Button>

                  <ButtonLoading
                    variant="default"
                    label="Salvar alterações"
                    loading={loadingForm}
                    type="submit"
                    disabled={
                      loadingForm ||
                      Boolean(errors.name) ||
                      Boolean(errors.description) ||
                      Boolean(errors.price) ||
                      Boolean(errors.periodicity)
                    }
                  />
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <Dialog open={openRemoveDialog} onOpenChange={() => setOpenRemoveDialog(false)}>
        <DialogContent className="gap-6">
          <DialogHeader className="w-fit">
            <DialogTitle>Remover Plano</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja remover o plano:{' '}
            <span className="font-semibold">{selectedItem?.name}</span>?
          </p>
          <DialogFooter className="flex-col">
            <Button
              variant="outline"
              onClick={() => {
                setOpenRemoveDialog(false);
              }}
            >
              Cancelar
            </Button>
            <ButtonLoading
              variant="default"
              label="Remover plano"
              loading={loadingForm}
              onClick={handleRemovePlan}
            >
              Remover
            </ButtonLoading>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PrivateAdminRoute>
  );
}
